import React from 'react';
import { KbSection, Callout, StepList } from '../../components/knowledge/primitives.jsx';

// Clinic-mailbox sub-identity guides. Each is gated to the tier that acts on it:
//  - clinic-signin       → the shared clinic login itself (role ClinicMailbox)
//  - clinic-staff-admin  → supervisors who approve names, reset PINs, edit staff
//  - case-sharing        → case editors who grant temporary access to a case
// So a reader only ever sees the part of the feature their access level uses.

function ClinicSignInBody() {
  return (
    <>
      <KbSection title="One clinic login, many people">
        <p>
          Your clinic signs in with a single shared mailbox account (for example
          {' '}<strong>yourclinic@therapieclinic.com</strong>). Because several people use it,
          the app asks <strong>who you are</strong> before it opens: you pick your name and
          enter a personal PIN. Everything you then do is recorded under your name, not just
          "the clinic".
        </p>
      </KbSection>

      <KbSection title="Signing in" delay={1}>
        <StepList steps={[
          <>Open the app on the clinic device and sign in with the shared clinic account.</>,
          <>On the <strong>"Who's signing in?"</strong> screen, choose your name from the dropdown.</>,
          <>Enter your <strong>6-digit PIN</strong> on the keypad and press <strong>Unlock</strong>.</>,
          <>The first time, you'll <strong>set up your PIN</strong> instead (choose it, then confirm). After that it's the PIN you enter each time.</>,
        ]} />
      </KbSection>

      <KbSection title="If your name isn't in the list" delay={2}>
        <p>
          Choose <strong>Register a new name</strong> in the dropdown, enter your full name and
          job title, and press <strong>Request</strong>. A supervisor confirms it. Once approved,
          your name appears in the list and you set up your PIN on next sign-in.
        </p>
        <Callout tone="info">
          You can't sign in until a supervisor has approved your name — this is how they confirm
          it's really you behind the shared login.
        </Callout>
      </KbSection>

      <KbSection title="Forgot your PIN" delay={3}>
        <p>
          On the PIN screen, choose <strong>Forgot your PIN?</strong>. That sends a reset request
          to your supervisor. When they action it, your PIN is cleared and you set a new one the
          next time you sign in. There's no self-service reset — only a supervisor can do it.
        </p>
      </KbSection>

      <KbSection title="Handing the device to a colleague" delay={4}>
        <p>
          Use <strong>Lock</strong> in the user menu (bottom-left) when you step away. It returns
          to the "Who's signing in?" screen so the next person signs in as themselves. Always Lock
          rather than leaving your session open on a shared device.
        </p>
      </KbSection>

      <KbSection title="What you can see" delay={5}>
        <p>
          You see the cases <strong>you reported</strong> — not your colleagues' cases, and no other
          clinic's cases. A supervisor or manager can <strong>share a specific case</strong> with you
          when they need your help (for example, to add an update while the manager is away); a
          shared case then appears in your list until the share is removed.
        </p>
      </KbSection>
    </>
  );
}

function ClinicStaffAdminBody() {
  return (
    <>
      <KbSection title="What this covers">
        <p>
          Clinics with no manager on the system report through a <strong>shared mailbox login</strong>,
          split into named people ("sub-identities"). You confirm who those people are, reset their
          PINs, and manage the roster. The shared mailbox itself has <strong>no personal PIN</strong> —
          its named staff do.
        </p>
      </KbSection>

      <KbSection title="Approving a name" delay={1}>
        <p>
          When someone registers at a clinic, their request appears in <strong>Notifications</strong>
          {' '}(the sidebar item with a red dot). Each item is tagged:
        </p>
        <StepList steps={[
          <><strong>New name</strong> — a person asking to be confirmed. <strong>Approve</strong> lets them set up a PIN and sign in; <strong>Deny</strong> rejects it.</>,
          <><strong>PIN reset</strong> — someone who forgot their PIN. <strong>Reset PIN</strong> clears it so they set a fresh one on next sign-in; <strong>Dismiss</strong> declines.</>,
        ]} />
        <Callout tone="warn">
          Approving a name is how you confirm identity past the shared login. Only approve people you
          recognise as working at that clinic.
        </Callout>
      </KbSection>

      <KbSection title="Editing a clinic's staff" delay={2}>
        <p>
          In <strong>Admin ▸ Users &amp; access</strong>, a clinic mailbox row shows
          {' '}<strong>Per-staff PINs</strong> and an <strong>Edit staff</strong> action (not "Reset PIN",
          since the mailbox has no personal PIN). Edit staff opens the clinic's roster where you can:
        </p>
        <StepList steps={[
          <><strong>Reset PIN</strong> for a person — clears their PIN so they set a new one.</>,
          <><strong>Remove</strong> — disables the person (keeps the record, drops their PIN). <strong>Re-activate</strong> brings them back.</>,
          <><strong>Delete</strong> — permanent removal: name, PIN, sessions and case shares are erased. Cases they already reported are kept. This can't be undone.</>,
          <><strong>Add staff member</strong> — register someone directly as active, without the request/approval round-trip.</>,
        ]} />
      </KbSection>

      <KbSection title="Sharing a case with clinic staff" delay={3}>
        <p>
          On any case you can open, the <strong>Share</strong> button (top of the case) grants a
          registered clinic staff member temporary access to that one case — for updates when the
          manager is away. See the "Sharing a case with clinic staff" article for the details.
        </p>
      </KbSection>

      <KbSection title="Who can do this" delay={4}>
        <p>
          Approving names and PIN resets, and Edit staff, are available to <strong>Supervisors</strong>
          {' '}(and Super Admins) for now. Sharing a case is available to <strong>Heads of RO and
          Supervisors</strong> for now. Both may be extended to Clinic Managers later.
        </p>
        <Callout tone="info">
          The current approvals inbox shows requests across all clinics. If this is opened up to
          Heads of RO or Clinic Managers, it will be scoped to the clinics they already oversee.
        </Callout>
      </KbSection>
    </>
  );
}

function CaseSharingBody() {
  return (
    <>
      <KbSection title="What sharing does">
        <p>
          Clinic staff normally see only the cases they reported. When a manager is away and a case
          needs an update, you can <strong>share that specific case</strong> with a registered member
          of the case's clinic. They get access to <strong>this case only</strong> — never the clinic's
          other cases — until you remove the share.
        </p>
      </KbSection>

      <KbSection title="Sharing a case" delay={1}>
        <StepList steps={[
          <>Open the case and press <strong>Share</strong> at the top (the count beside it shows how many people it's shared with).</>,
          <>In the popover, choose a staff member from the clinic's registered list and press <strong>Share</strong>.</>,
          <>They can now open and update the case. It appears in their case list.</>,
          <>To end access, press <strong>Remove</strong> next to their name. It takes effect immediately.</>,
        ]} />
      </KbSection>

      <KbSection title="When no one is available to share with" delay={2}>
        <p>
          The list only offers <strong>registered, approved</strong> staff of that clinic. If it says
          no staff are registered, the clinic hasn't onboarded anyone yet — they register from the
          clinic sign-in screen, and a supervisor approves them (see "Clinic staff: approvals, PINs
          &amp; access"). Once approved, they appear here.
        </p>
        <Callout tone="info">
          Sharing is deliberate and per-case. It never widens a person's access to the rest of the
          clinic's cases, and a removed share is gone at once.
        </Callout>
      </KbSection>
    </>
  );
}

export const CLINIC_ACCESS_ARTICLES = [
  {
    id: 'clinic-signin',
    title: 'Signing in at a shared clinic device',
    category: 'getting-started',
    summary: 'Pick your name, set up or enter your PIN, register if you\'re new, and lock the device for the next person.',
    keywords: ['clinic', 'mailbox', 'shared login', 'sign in', 'pin', 'register', 'forgot pin', 'lock', 'sub-identity'],
    visibleTo: (u) => u?.role === 'ClinicMailbox',
    Body: ClinicSignInBody,
  },
  {
    id: 'clinic-staff-admin',
    title: 'Clinic staff: approvals, PINs & access',
    category: 'administration',
    summary: 'Approve names and PIN resets from Notifications, and manage a clinic\'s roster from Edit staff.',
    keywords: ['clinic', 'mailbox', 'approve', 'pin reset', 'edit staff', 'roster', 'remove', 'delete', 'notifications', 'supervisor'],
    visibleTo: (u) => !!u?.canManageClinicAccess,
    Body: ClinicStaffAdminBody,
  },
  {
    id: 'case-sharing',
    title: 'Sharing a case with clinic staff',
    category: 'working-cases',
    summary: 'Grant a registered clinic staff member temporary access to one case, and remove it when done.',
    keywords: ['share', 'case share', 'temporary access', 'clinic staff', 'delegate', 'cover', 'manager away'],
    visibleTo: (u) => !!u?.canShareCaseAccess,
    Body: CaseSharingBody,
  },
];
